<template>
  <RevAccordionItem>
    <template #prefix><IconSmartphone /></template>
    <template #title>{{ i18n(translations.title) }}</template>
    <template #description>{{ product?.title }}</template>
    <template #body>
      <div class="text-static-default-low">
        <div class="mb-16">
          <p>
            {{ i18n(translations.quantity, { quantity: orderline.quantity }) }}
          </p>
          <p>{{ i18n(translations.price, { price }) }}</p>
          <p v-if="product?.sku">
            <FormattedMessage :definition="translations.sku">
              <template #productLink>
                <RevLink target="_blank" :to="productLink">
                  <span class="text-static-default-low body-1-bold">
                    {{ product?.sku }}
                  </span>
                </RevLink>
              </template>
            </FormattedMessage>
          </p>
        </div>

        <p>
          {{ i18n(translations.grade, { grade: product?.backboxGrade.name }) }}
        </p>
        <p v-if="product?.isNewBatteryDevice">
          {{ i18n(translations.newBatteryDevice) }}
        </p>
        <p v-if="serialNumber">
          {{ i18n(translations.serialNumber, { serialNumber }) }}
        </p>
        <p v-if="imei">
          {{ i18n(translations.imei, { imei }) }}
        </p>
        <p v-if="product?.isYakuzaPhone">
          {{ i18n(translations.yakuzaPhone) }}
        </p>
        <p v-if="product?.comment">{{ product.comment }}</p>
      </div>
    </template>
  </RevAccordionItem>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevLink } from '@ds/components/Link'
import { IconSmartphone } from '@ds/icons/IconSmartphone'

import type { Orderline, Product } from '~/api/care-folders/types/care-folder'
import { ROUTE_NAMES } from '~/constants/routes'

import translations from './ProductDetails.translations'

const i18n = useI18n()
const locale = useI18nLocale()

const props = defineProps<{
  orderline: Orderline
  product: Product
}>()

const price = computed(() => {
  const shipping =
    parseInt(props.orderline.shippingPrice, 10) > 0
      ? ` + ${i18n(translations.shippingPrice, { shippingPrice: props.orderline.shippingPrice })}`
      : ''

  const tax =
    parseInt(props.orderline.salesTaxPrice, 10) > 0
      ? ` + ${i18n(translations.salesTaxPrice, { salesTaxPrice: props.orderline.salesTaxPrice })}`
      : ''

  return `${props.orderline.price}${shipping}${tax}`
})

const productLink = computed(() => {
  if (props.product?.akeneoId && props.product?.productSlug) {
    return {
      name: ROUTE_NAMES.FRONT_OFFICE.PRODUCT.HOME,
      params: {
        locale,
        slugV2: props.product.productSlug,
        uuid: props.product.akeneoId,
      },
    }
  }

  return ''
})

const imei = computed(() => {
  if (isEmpty(props.orderline.itemIdentifier)) return null
  const { type, value } = props.orderline.itemIdentifier

  return type === 'IMEI' ? value : null
})

const serialNumber = computed(() => {
  if (isEmpty(props.orderline.itemIdentifier)) return null
  const { type, value } = props.orderline.itemIdentifier

  return type === 'SERIAL_NUMBER' ? value : null
})
</script>
